import { isClient } from '@vueuse/shared';
import { toFixed, times, toFormat, toFormatUnit, lt, gt, div } from './number';
import { getOriginPath, i18n } from '@/lang/i18n';
import http from './axios';
import { pinia } from '@/store/index';
import { getCookie } from '@/utils/cookie';
import { cookieDomain } from '@/config/setup';
import { useConfigStore } from '@/store/config';
import { UPNG } from './upng';
import { checkInMiniApp } from '@/utils/miniApp';
import cookiePlus from '@/utils/cookiePlus';

const configStore = useConfigStore(pinia);
/**
 * 法币估值
 * 计算公式= rates[token][moneys[choose][1]]*value
 * @param {object} rates 所有汇率 { BTC:{ BTC:1, CNY: 4000, USD: 3000} }
 * @param {number} value token的值, 如 33.2
 * @param {string} token tokenId, 如BTC，ETH
 * @param {string} choose 转换成何种法币，如 en-us, zh-cn， 默认en-us
 * @param {bool} suffix 是否返回后货币符号，默认false返回如[¥, 100]，为true时返回如[CNY, 100]
 * @return {array} [法币标志,法币估值], 如 ['usd',2323.231] , 法币估值保留2位小数，如果小于0.01，保留5位小数, 如果估值为负数, value返回'--';
 */
export const currencyValue = (
  rates,
  value,
  token,
  choose = isClient ? (window.localStorage.legal_flag || '') : '',
  suffix = false
) => {
  const money = configStore.supportLegalCurrency;
  if (
    !rates ||
    !money ||
    !money.length ||
    (!value && value !== 0) ||
    !token ||
    !rates[token]
  ) {
    return suffix ? ['', '0.00', ''] : ['', '0.00'];
  }

  let moneys = {};
  money.map(item => {
    moneys[item.currency.toLowerCase()] = [item.currencyLabel, item.currency];
  });
  // 要获取的法币是否有汇率，如果没有，默认获取en-us
  const realChoose =
    moneys[choose.toLowerCase()] && moneys[choose.toLowerCase()][0]
      ? choose.toLowerCase()
      : 'usd';

  if (!moneys[realChoose]) {
    // 折算不出来都返成0.00
    return suffix ? ['', '0.00', ''] : ['', '0.00'];
  }

  const name = moneys[realChoose][0];
  const endName = moneys[realChoose][1];
  let v = rates[token.toUpperCase()][moneys[realChoose][1]];
  // 选择币对的汇率不存在
  if (!v) {
    return suffix ? [name, '0.00', endName] : [name, '0.00'];
  }

  v = times(v, value);
  let fix = v > 0 && v - 0.1 < 0 && Number(v) !== 0 ? 5 : 2;
  if (v > 0) {
    if (v - 0.00001 < 0) {
      fix = 8;
    }
    if (v - 0.00000001 < 0) {
      fix = 10;
    }
    if (v - 0.0000000001 < 0) {
      fix = 12;
    }
  }
  v = toFixed(v, fix);
  // 保留不格式化的值 币种排序使用
  let _v = toFixed(v, fix);

  // if (Number(v) < 0) {
  //   v = '--';
  // } else {
  //   v = toFormat(v, fix);
  // }

  v = toFormat(v, fix);

  return suffix ? [name, v, endName] : [name, v, _v];
};

/**
 * 估值单位转换 比如将USDT转化为BTC 用来处理极端情况下，btc估值为0，但是usdt估值不为0的情况
 * 会损失一定的精度
 * @param {object} rates 所有汇率 { BTC:{ BTC:1, CNY: 4000, USD: 3000} }
 * @param {number} value token的值, 如 33.2
 * @param {string} sourceToken 转换前tokenId, 如BTC，ETH
 * @param {string} targetToken 转换后tokenId, 如BTC，ETH
 */
export const convertValuationUnit = (rates, value, sourceToken, targetToken) => {
  let convertRate = rates[sourceToken][targetToken];
  const v = times(value, convertRate);

  return v;
};

/**
 *
 * @param {string} value 值
 * @param {boolean} isDecimal 是否有小数
 * @param {number | string} accuracy 小数精度
 * @param {number | string} integer 数据位数
 * @returns
 */
export const filterNumberAndDot = (value, isDecimal = false, accuracy = 0, integer = 0) => {
  if (!value) {
    return '';
  }

  value = value.replace('。', '.');

  if (value.length >= 2) {
    const n = value.substring(0, 1);
    const i = value.substring(1, 2);

    if (n === '0' && i !== '.') {
      return '0';
    }
  }

  // 没有小数
  if (!isDecimal) {
    value = value.replace(/[^\d]/g, '');

    if (integer) {
      return value.length > integer ? value.slice(0, integer) : value;
    }

    return value;
  }

  // 没有点，或点在第一位
  if (value.indexOf('.') === -1 || value.indexOf('.') === 0) {
    value = value.replace(/[^\d]/g, '');

    if (integer) {
      return value.length > integer ? value.slice(0, integer) : value;
    }

    return value;
  }

  value = value
    .replace(/[^\d.]/g, '') // 将非数字和点以外的字符替换成空
    .replace(/^\./g, '') // 验证第一个字符是数字而不是点
    .replace(/\.{2,}/g, '.') // 出现多个点时只保留第一个
    .replace('.', '$#$') // 1、将数字的点替换成复杂字符$#$
    .replace(/\./g, '') // 2、将字符串的点直接清掉
    .replace('$#$', '.'); // 3、将复杂字符再转换回点

  const valueArr = value.split('.');

  if (integer) {
    valueArr[0] = valueArr[0].length > integer ? valueArr[0].slice(0, integer) : valueArr[0];
  }

  if (valueArr[1].length <= Number(accuracy)) {
    return valueArr.join('.');
  }

  valueArr[1] = valueArr[1].slice(0, Number(accuracy));

  return valueArr.join('.');
};

/**
 * 浮点类型转整型精度
 * @param {*} float
 */
export const floatToPrecisionInt = floatString => {
  if (!floatString) {
    return 0;
  }

  const strArray = String(floatString).split('.');

  return strArray.length > 1 ? strArray[1].length : 0;
};

/**
 * 获取交易对精度
 *
 * @param {*} symbol 交易对信息
 * @returns Array
 */
export const getSymbolPrecision = symbol => {
  const { digitMerge = '' } = symbol;
  const options = digitMerge.split(',').map(item => {
    const strArray = item.split('.');
    // const intLength = strArray[0].length - 1;
    // const intValue = intLength === 0 ? 0 : -intLength;

    return {
      label: item,
      value: item,
      precision: strArray.length > 1 ? strArray[1].length : 0
    };
  });

  // 从小到大排序
  return options.sort((a, b) => lt(a.label, b.label) ? -1 : 1);
};

/**
 * 数量加单位（K,M,B）、处理精度
 * 目前是超过4位加单位
 *
 * @param {string} amount 数量
 * @param {number} precision 精度
 * @param {number} length 加单位触发位数
 */
export const amountFormat = (amount = '', precision, unitLength = 6) => {
  // TODO 可以更严格点，先校验下是否为数字

  if (String(amount).split('.')[0].length > unitLength) {
    return toFormatUnit(amount, 2).replace(' ', '');
    // return toFormatUnit(amount, precision != 0 ? precision : 2, 0).replace(' ', '');
  } else {
    return toFormat(amount, precision, 0);
  }
};

/**
 * 简短地址
 * @param {string} address 地址字符串
 * @returns {string} 格式化后的字符串，例如：0x...abcdef
 */
export const shortAddress = address => {
  return address.replace(/(.{4}).+(.{6})$/, '$1...$2');
};

// 尺寸变化事件
export const resizeObserver = cb => new ResizeObserver(cb);

/**
 *
 * @param {*} scene 使用场景
 */
export const generateCaptcha = async scene => {
  // 没有传场景
  if (!scene) {
    return false;
  }

  const sceneArr = [
    'login', // 登录
    'register', // 注册
    'reset_password', // 忘记密码
    'rebind_email', // 换绑邮箱
    'rebind_mobile' // 换绑手机
  ];

  let token = '';

  // 不在当前场景内
  if (!sceneArr.includes(scene)) {
    return false;
  }

  if (!grecaptcha) {
    return false;
  }

  token = await grecaptcha.enterprise.execute('6LenB_QiAAAAAOLAvSvN_2mZAb6ZOwYa5mwTCWk2', { action: scene });

  return token;
};

/**
 * bex颜色转为rgba
 * @param String #ff00ff #f0f
 * @param Number a 0-1
 * @return String rgba(r,g,b,a)
 */
export const hexToRgba = (hex, a = 1) => {
  if (!hex || hex.indexOf('#') == -1) {
    return 'rgba(0,0,0,0)';
  }
  if (hex.length != 7 && hex.length != 4) {
    console.error(`${hex} is not hex color`);
    return 'rgba(0,0,0,0)';
  }
  let s = hex.replace('#', '').match(/^(..?)(..?)(..?)/);
  return `rgba(${parseInt(
    '0x' + s[1] + (s[1].length == 1 ? s[1] : '')
  )},${parseInt('0x' + s[2] + (s[2].length == 1 ? s[2] : ''))},${parseInt(
    '0x' + s[3] + (s[3].length == 1 ? s[3] : '')
  )},${Number(a)})`;
};

/**
 * 数组排重
 * @param key 根据key进行排重
 * @param ar  数组
 * @param time 如果重复，根据time进行保留最新
 */
export const excludeRepeatArray = (key, ar, time) => {
  let obj = {};
  if (
    !key ||
    !ar ||
    Object.prototype.toString.call(ar) !== '[object Array]'
  ) {
    return;
  }
  ar.forEach(item => {
    if (obj[item[key]]) {
      // 如果重复，保留time最新的数据
      if (time && item[time] - obj[item[key]][time] >= 0) {
        obj[item[key]] = item;
      }
    } else {
      obj[item[key]] = item;
    }
  });
  const result = [];
  for (let k in obj) {
    result.push(obj[k]);
  }
  return result;
};

export const delay = timeout => {
  return new Promise(resolve => {
    setTimeout(resolve, timeout);
  });
};

export const isApp = () => {
  const isToobitApp = navigator.userAgent.toLowerCase().indexOf('toobit') > -1;
  let isFlutter = window.frames.length !== window.parent.frames.length;
  return isFlutter || (isClient ? isToobitApp : false);
};
export const isFlutter = () => {
  const isFlutter =
    window.frames.length !== window.parent.frames.length;
  return isFlutter;
};

export const isTg = () => {
  return localStorage.getItem('inMiniApp') === 'true';
};

export const isPC = () => {
  if (import.meta.env.SSR) return true;
  let userAgentInfo = window.navigator.userAgent,
    Agents = ['Android', 'iPhone', 'SymbianOS', 'Windows Phone', 'iPad', 'iPod', 'rv:1.2.3.4', 'windows mobile', 'midp'],
    flag = true;
  for (let v = 0; v < Agents.length; v++) {
    if (userAgentInfo.indexOf(Agents[v]) >= 0) {
      flag = false;
      break;
    }
  }
  return flag;
};
// 是否是安卓
export const isAndroid = () => {
  return /android/i.test(navigator.userAgent);
};

// 是否是ios
export const isIOS = () => {
  return /(iPhone|iPad|iPod|iOS)/i.test(navigator.userAgent);
};
// 20221201 =>  2022-12-01
export const sliceTime = time => {
  if (time) {
    return `${time.slice(0, 4)}-${time.slice(4, 6)}-${time.slice(6, 8)}`;
  }
  return time;
};
export const showAssets = assets => {
  let index = assets.indexOf('(');
  if (index > -1) {
    return assets.slice(index + 1, -1);
  }
  return assets;
};

export const countDown = (end, now, type) => {
  const msec = end - now;
  let day = parseInt(msec / 1000 / 60 / 60 / 24);
  let hr = parseInt(msec / 1000 / 60 / 60 % 24);
  let min = parseInt(msec / 1000 / 60 % 60);
  let sec = parseInt(msec / 1000 % 60);
  hr = hr > 9 ? hr : '0' + hr;
  min = min > 9 ? min : '0' + min;
  sec = sec > 9 ? sec : '0' + sec;
  if (type == 'pop') {
    if (msec < 0) {
      day = '0';
      hr = '00';
      min = '00';
      sec = '00';
    }
    return {
      d: day,
      h: hr,
      m: min,
      s: sec
    };
  }
  if (msec < 0) {
    return '00:00:00';
  }
  return `${day > 0 ? day + ':' : ''}${hr}:${min}:${sec}`;
};

export const gaTag = (value, data = {}) => {
  if (import.meta.env.SSR) return;
  if (!value) return;
  try {
    window && window.gtag && window.gtag('event', value, data);
  } catch (error) {}
};

export const fbq = (value, data = {}) => {
  if (!value) return;
  try {
    window && window.fbq && window.fbq('track', value, data);
  } catch (error) {}
};
export const flutterPostMessage = str => {
  if (!isApp()) return;
  window.JumpFlutter?.postMessage(str);
  window.parent?.postMessage(str, '*');
  // console.log(str);
};
// app合约交易
export const toContractTransaction = symbolId => {
  if (!isApp()) return;
  // flutter 端跳转页面 用 window.parent?.postMessage
  // window.parent?.postMessage('app://www.toobit.com/copytrading', '*');
  if (symbolId) {
    flutterPostMessage(`app://www.toobit.com/contract?symbolId=${symbolId}`);
  } else {
    flutterPostMessage('app://www.toobit.com/contract');
  }
};
// app首页
export const toAppHome = () => {
  if (!isApp()) return;
  flutterPostMessage('app://www.toobit.com/home');
};
// app现货交易
export const toSpotTransaction = symbolId => {
  if (!isApp()) return;
  flutterPostMessage(`app://www.toobit.com/spot?symbolId=${symbolId}`);
};
export const toAppTransfer = () => {
  if (!isApp()) return;
  flutterPostMessage('app://www.toobit.com/transfer');
};
// app KYC
export const toAppKYC = () => {
  if (!isApp()) return;
  flutterPostMessage('app://www.toobit.com/kyc_page');
};
// 调用app登录
export const toAppLogin = () => {
  if (!isApp()) return;
  let url = 'app://www.toobit.com/login';
  const activity_id = cookiePlus.read('activity_id');
  if (activity_id) {
    url += `?activity_id=${activity_id}`;
  }
  flutterPostMessage(url);
};
// 调用app注册
export const toAppRegister = () => {
  if (!isApp()) return;
  let url = 'app://www.toobit.com/register';
  const activity_id = cookiePlus.read('activity_id');
  if (activity_id) {
    url += `?activity_id=${activity_id}`;
  }
  flutterPostMessage(url);
};
// 调用app分享
export const appShare = (image, url) => {
  if (!isApp()) {
    const downloadLink = document.createElement('a');
    downloadLink.download = 'image.png';
    downloadLink.href = image.replace(/^data:image\/[^;]/, 'data:image/png;');
    downloadLink.click();
  } else {
    let params = `image=${image}`;
    if (url) {
      params = `${params}&url=${url}`;
    }
    flutterPostMessage(`app://www.toobit.com/share?${params}`);
  }
};
// 调用app跟单
export const appCopyTrading = () => {
  if (!isApp()) return;
  flutterPostMessage('app://www.toobit.com/copytrading');
};

// 调用app交易机器人
export const appBotsTradeMain = () => {
  if (!isApp()) return;
  flutterPostMessage('app://www.toobit.com/botsTradeMain');
};

// 调用app带单
export const appCopyTradingMain = () => {
  if (!isApp()) return;
  flutterPostMessage('app://www.toobit.com/copyTradingMain?page=1');
};

// app打开新的webview
export const appWebView = (url = '') => {
  if (!isApp()) return;
  flutterPostMessage(`app://www.toobit.com/webView?url=${url}`);
};
// 跳转app 链接
export const appLink = (url = '') => {
  if (!isApp()) return;
  flutterPostMessage(`app://www.toobit.com/${url}`);
};
// 跳转app充值
export const appDeposit = (coin = 'USDT') => {
  if (!isApp()) return;
  flutterPostMessage(`app://www.toobit.com/deposit?tokenId=${coin.toLocaleUpperCase()}`);
};
// /coupon_center
// 跳转app福利中心
export const appReward = () => {
  if (!isApp()) return;
  flutterPostMessage('app://www.toobit.com/coupon_center');
};
// 调用app关闭h5
export const appCloseH5 = () => {
  if (!isApp()) return;
  flutterPostMessage('app://www.toobit.com/close');
};
// 信用卡买币
export const appCardBuy = () => {
  if (!isApp()) return;
  flutterPostMessage('app://www.toobit.com/cardBuy');
};
// 卡券中心
export const appCoupon = () => {
  if (!isApp()) return;
  flutterPostMessage('app://www.toobit.com/coupon_center');
};
// app登录回调
export const isAppLogin = () => {
  if (!isApp()) return;
  window.isAppLogin = message => {
    if (message) {
      setTimeout(() => {
        window.location.reload();
        appLoginCookie();
      }, 500);
    }
  };
};
// 站外打开链接
export const appBrowser = url => {
  if (!isApp()) return;
  flutterPostMessage(`app://www.toobit.com/browser?url=${url}`);
};

export const getFullPath = () => {
  return `${encodeURIComponent(getOriginPath(window.location.pathname))}${encodeURIComponent(window.location.search)}`;
};
export const getBackUrl = () => {
  return `/login?backurl=${getFullPath()}`;
};
// 登录和注册路由逻辑封装
export const getBackPath = (path, backurl) => {
  return `${path}?backurl=${backurl || getFullPath()}`;
};
// 登录和注册路由逻辑封装
// export const getBackPath = (path, crossPlatform, locale) => {
//   const platformOrigin = {
//     web: { to: envHost, from: h5Host },
//     h5: { to: h5Host, from: envHost }
//   };

//   if (crossPlatform) {
//     const originItem = platformOrigin[crossPlatform];
//     const localePath = locale ? `/${locale}` : '';
//     const inviteCode = window.location.search ? window.location.search + '&' : '?';
//     return `${originItem.to}${localePath}${path}${inviteCode}backurl=${encodeURIComponent(originItem.from)}${localePath}${encodeURIComponent(getOriginPath(window.location.pathname))}${encodeURIComponent(window.location.search)}`;
//   }

//   return `${path}?backurl=${encodeURIComponent(getOriginPath(window.location.pathname))}${encodeURIComponent(window.location.search)}`;
// };
// app粘贴
export const appCopy = text => {
  if (!isApp()) return;
  flutterPostMessage(`app://www.toobit.com/copy?text=${text}`);
};
export const toAppUrl = url => {
  // 判断 url 开头是不是 app://
  if ((/^app:\/\//).test(url)) {
    flutterPostMessage(url);
    return;
  }
  if (checkInMiniApp()) {
    window.location.href = url;
  } else {
    window.open(url);
  }
};

export const leftPad = num => {
  return num < 10 ? '0' + num : '' + num;
};
// 毫秒 时间戳转化时间  tpl 控制格式
export const dateFormatter = (ts, tpl = 'Y-M-D H:m:s', utc = false) => {
  if (!ts) {
    return '--';
  }
  const date = new Date(Number(ts));
  const dateObj = utc
    ? {
      Y: date.getUTCFullYear(),
      M: leftPad(date.getUTCMonth() + 1),
      D: leftPad(date.getUTCDate()),
      H: leftPad(date.getUTCHours()),
      m: leftPad(date.getUTCMinutes()),
      s: leftPad(date.getUTCSeconds())
    }
    : {
      Y: date.getFullYear(),
      M: leftPad(date.getMonth() + 1),
      D: leftPad(date.getDate()),
      H: leftPad(date.getHours()),
      m: leftPad(date.getMinutes()),
      s: leftPad(date.getSeconds())
    };
  for (var key in dateObj) {
    tpl = tpl.replace(key, dateObj[key]);
  }
  return tpl;
};
// 秒转时间
export const unixFormatter = (unix, tpl) => {
  return dateFormatter(unix * 1000, tpl);
};

/**
 * 外链图片url转base64
 * @param {*} url
 * @returns
 */
export const getURLBase64 = url => {
  return new Promise(async resolve => {
    const newUrl = url + '?v=' + Date.now();
    const res = await http.get(newUrl, { withCredentials: false, responseType: 'blob' });
    if (res.success) {
      const imgSrc = URL.createObjectURL(res.data);
      resolve(imgSrc);
    } else {
      resolve('');
    }
  });
};

// APP登录状态写入Cookie
export const appLoginCookie = () => {
  // cookieDomain
  if (isApp()) {
    if (getCookie('au_token')) {
      document.cookie = `account_id=${getCookie('account_id')};path=/;domain=${cookieDomain}`;
      document.cookie = `user_id=${getCookie('user_id')};path=/;domain=${cookieDomain}`;
      document.cookie = `c_token=${getCookie('c_token')};path=/;domain=${cookieDomain}`;
      document.cookie = `au_token=${getCookie('au_token')};path=/;domain=${cookieDomain}`;
      document.cookie = `d=${getCookie('d')};path=/;domain=${cookieDomain}`;
      return true;
    }
  }
  return false;
};

// 根据时间戳返回 几分钟前 几小时前 几天前 几月前 几年前
export const messageTime = time => {
  const now = new Date().getTime();
  const diff = now - time;
  const day = Math.floor(diff / (24 * 3600 * 1000));
  const hour = Math.floor(diff / (3600 * 1000));
  const min = Math.floor(diff / (60 * 1000));
  const month = Math.floor(day / 30);
  const year = Math.floor(month / 12);
  if (year > 0) {
    return i18n.global.t('%s 年前').replace('%s', year);
  } else if (month > 0) {
    return i18n.global.t('%s 月前').replace('%s', month);
  } else if (day > 0) {
    return i18n.global.t('%s 天前').replace('%s', day);
  } else if (hour > 0) {
    return i18n.global.t('%s 小时前').replace('%s', hour);
  } else if (min > 0) {
    return i18n.global.t('%s 分钟前').replace('%s', min);
  } else {
    return i18n.global.t('刚刚');
  }
};
// 根据时间戳返回 几分钟前 几小时前 几天前 几月前 几年前
export const runTime = time => {
  if (!time) {
    return '-';
  }
  const diff = time;
  const day = Math.floor(diff / (24 * 3600 * 1000));
  const hour = Math.floor(diff / (3600 * 1000));
  const min = Math.floor(diff / (60 * 1000));
  if (day > 0) {
    return i18n.global.t('%s 天').replace('%s', day);
  } else if (hour > 0) {
    return i18n.global.t('%s 小时').replace('%s', hour);
  } else if (min > 0) {
    return i18n.global.t('%s 分钟').replace('%s', min);
  } else {
    return i18n.global.t('%s 分钟').replace('%s', '< 1');
  }
};

/**
 * 福利中心 倒计时
 * @param {*} end
 * @param {*} now
 * @returns
 */
export const tasksCountDown = (end, now, span = false) => {
  const msec = end - now;
  let hr = parseInt(msec / 1000 / 60 / 60);
  let min = parseInt(msec / 1000 / 60 % 60);
  let sec = parseInt(msec / 1000 % 60);
  hr = hr > 9 ? hr : '0' + hr;
  min = min > 9 ? min : '0' + min;
  sec = sec > 9 ? sec : '0' + sec;
  if (msec <= 1000) {
    return '00:00:00';
  }
  return span ? `<span>${hr}</span><em>:</em><span>${min}</span><em>:</em><span>${sec}</span>` : `${hr} : ${min} : ${sec}`;
};
export const addZero = num => {
  return num > 9 ? num : '0' + num;
};
/**
 * 福利中心2期 倒计时
 * @param {*} end
 * @param {*} now
 * @returns
 */
export const tasksCountDownV2 = (end, now, tpl = 'DayD Hr:Min:Sec') => {
  const msec = end - now > 0 ? end - now : 0;
  const dateObj = {
    Day: parseInt(msec / 1000 / 60 / 60 / 24),
    Hr: parseInt(msec / 1000 / 60 / 60 % 24),
    Min: parseInt(msec / 1000 / 60 % 60),
    Sec: parseInt(msec / 1000 % 60)
  };
  if (tpl === 'obj') {
    return {
      Day: addZero(dateObj.Day),
      Hr: addZero(dateObj.Hr),
      Min: addZero(dateObj.Min),
      Sec: addZero(dateObj.Sec)
    };
  }
  for (var key in dateObj) {
    tpl = tpl.replace(key, dateObj[key] > 9 ? dateObj[key] : '0' + dateObj[key]);
  }
  return tpl;
};

/**
 * 清除字符串中的代码注入
 *
 * @param {*} str
 * @returns
 */
export const clearScriptCode = str => {
  const decodeURL = decodeURIComponent(str).replace(/\s+/g, '');

  const scriptStrs = decodeURL?.toLowerCase().match(/^javascript.*/g);

  return scriptStrs?.length ? decodeURL?.toLowerCase().replace(scriptStrs[0], '') : str;
};

// 合约盈亏率展示
export const showRate = rate => {
  if (rate) {
    // 判断超过10000 显示10000+
    if (gt(rate, 10000)) {
      return '10000+';
    }
    // 判断小于 -100 显示-100
    if (lt(rate, -100)) {
      return '-100';
    }
  }
  return rate;
};

// 处理合约风险限额白名单
export const handleRiskLimitsWhite = riskLimits => {
  if (riskLimits && riskLimits.length) {
    const arr = [];
    let whiteObj = null;

    riskLimits.forEach(item => {
      const obj = {
        riskLimitAmount: item.riskLimitAmount,
        leverage: Number(toFixed(div(1, item.initialMargin), 0))
      };

      if (item.white) {
        whiteObj = obj;
      } else {
        arr.push(obj);
      }
    });

    if (whiteObj) {
      arr.forEach(item => {
        if (item.leverage === whiteObj.leverage) {
          item.riskLimitAmount = whiteObj.riskLimitAmount;
        }
      });
    }

    return arr;
  }

  return riskLimits;
};

export const getLocationParams = () => {
  if (isClient) {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = {};

    for (const [key, value] of searchParams.entries()) {
      queryParams[key] = value;
    }

    return queryParams;
  }

  return {};
};
// class success-success1 danger-danger1
export const countClass = str => {
  if (gt(str, 0)) {
    return 'success-success1';
  }
  if (lt(str, 0)) {
    return 'danger-danger1';
  }
  return '';
};

export const compressImage = async(file, ops = {}) => {
  const { width, height, quality = 0.8, noCompressIfLarger = true } = ops;

  let newFile = new File([], file.name, { type: file.type });

  const reader = new FileReader();
  reader.readAsDataURL(file);
  await new Promise((resolve, reject) => { reader.onload = resolve; reader.onerror = reject; });
  const image = new Image();
  image.src = reader.result;
  await new Promise((resolve, reject) => { image.onload = resolve; image.onerror = reject; });

  if (file.type.match('png')) {
    const arrayBuffer = await file.arrayBuffer();
    const decoded = UPNG.decode(arrayBuffer);
    const rgba8 = UPNG.toRGBA8(decoded);

    const compressed = UPNG.encode(
      rgba8,
      width || decoded.width,
      height || decoded.height,
      256 * quality
    );

    newFile = new File([compressed], file.name, { type: 'image/png' });
  } else {

    const canvas = document.createElement('canvas');
    canvas.width = width || image.width;
    canvas.height = height || image.height;
    canvas.getContext('2d').drawImage(image, 0, 0, width || image.width, height || image.height);
    newFile = base64toBlob(canvas.toDataURL(file.type, quality), file.name, file.type);
  }

  if (!noCompressIfLarger) {
    return newFile;
  }

  const newImage = file.size > newFile.size ? newFile : file;

  const newReader = new FileReader();
  newReader.readAsDataURL(newImage);
  await new Promise((resolve, reject) => { newReader.onload = resolve; newReader.onerror = reject; });

  return { file: newImage, width: width || image.width, height: height || image.height, src: newReader.result };
};

export const base64toBlob = (base64, filename, type = 'image/jpeg') => {
  var cleanedBase64 = base64.replace(/^data:image\/(png|jpeg|jpg);base64,/, '');
  var byteCharacters = atob(cleanedBase64);
  var byteNumbers = new Array(byteCharacters.length);
  for (var i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  var byteArray = new Uint8Array(byteNumbers);
  return new File([byteArray], filename, { type });
};

export const getHostFromUrl = url => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.host;
  } catch (error) {
    return null;
  }
};

export const isCopyTrade = () => {
  if (import.meta.env.SSR) return false;
  return window.location.pathname.toLocaleLowerCase().split('/').includes('futures-copy');
};

export const formatParams = (params = {}) => {
  if (isCopyTrade()) {
    return {
      ...params,
      business_type: 'COPY_TRADING_LEADER'
    };
  } else {
    return params;
  }

};

export const registerWithActivityId = value => {
  cookiePlus.write({
    name: 'activity_id',
    value,
    day: 1, // 24小时
    domain: window.location.hostname.split('.').slice(-2).join('.')  // 获取根域名
  });
};

/**
 * 统一URL域名工具函数
 * 将指定域名模式的URL替换为当前访问域名，用于多域名支持
 * @param {string} url - 需要处理的URL
 * @param {Object} options - 配置选项
 * @param {string|string[]} options.domainPatterns - 需要替换的域名模式，支持字符串或数组
 * @param {string} options.targetHost - 目标域名，默认为当前访问域名
 * @param {boolean} options.onlyInProduction - 是否只在生产环境执行，默认false
 * @param {boolean} options.preserveSubdomain - 是否保留子域名，默认true
 * @returns {string} 处理后的URL，如果处理失败则返回原URL
 */
export const normalizeUrlDomain = (url, options = {}) => {
  // 参数默认值
  const {
    domainPatterns = ['toobit.'],
    targetHost = window.location.host,
    onlyInProduction = true,
    preserveSubdomain = true
  } = options;

  // 检查是否需要在当前环境执行
  if (onlyInProduction && import.meta.env.MODE !== 'production') {
    return url;
  }

  // 检查URL是否有效
  if (!url || typeof url !== 'string') {
    return url;
  }

  try {
    // 解析URL
    const parsedUrl = new URL(url);
    const currentHost = parsedUrl.host;

    // 如果已经是目标域名，直接返回
    if (currentHost === targetHost) {
      return url;
    }

    // 统一处理域名模式为数组
    const patterns = Array.isArray(domainPatterns) ? domainPatterns : [domainPatterns];

    // 检查是否匹配任一域名模式
    const shouldReplace = patterns.some(pattern => currentHost.includes(pattern));

    if (!shouldReplace) {
      return url;
    }

    // 替换域名
    if (preserveSubdomain) {
      // 保留子域名结构
      const targetParts = targetHost.split('.');
      const currentParts = currentHost.split('.');

      // 如果目标域名和当前域名都有子域名，保持子域名层级一致
      if (targetParts.length >= 2 && currentParts.length >= 2) {
        parsedUrl.host = targetHost;
      } else {
        parsedUrl.host = targetHost;
      }
    } else {
      // 直接替换为目标域名
      parsedUrl.host = targetHost;
    }

    return parsedUrl.toString();

  } catch (error) {
    console.warn('URL域名统一处理失败:', error, 'URL:', url);
    return url;
  }
};

/**
 * 批量处理URL数组的域名统一
 * @param {string[]} urls - URL数组
 * @param {Object} options - 配置选项，同normalizeUrlDomain
 * @returns {string[]} 处理后的URL数组
 */
export const normalizeUrlsDomain = (urls, options = {}) => {
  if (!Array.isArray(urls)) {
    return urls;
  }

  return urls.map(url => normalizeUrlDomain(url, options));
};

/**
 * 处理对象中的URL字段域名统一
 * @param {Object} obj - 包含URL字段的对象
 * @param {string|string[]} urlFields - 需要处理的URL字段名，支持字符串或数组
 * @param {Object} options - 配置选项，同normalizeUrlDomain
 * @returns {Object} 处理后的对象
 */
export const normalizeObjectUrlsDomain = (obj, urlFields, options = {}) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const fields = Array.isArray(urlFields) ? urlFields : [urlFields];
  const result = { ...obj };

  fields.forEach(field => {
    if (result[field]) {
      if (Array.isArray(result[field])) {
        result[field] = normalizeUrlsDomain(result[field], options);
      } else if (typeof result[field] === 'string') {
        result[field] = normalizeUrlDomain(result[field], options);
      }
    }
  });

  return result;
};
